import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useRestClient } from '../../../core/api/RestClientContext';
import { AdminUserServerLinkDto, SyncStatus } from '../../../core/models/sync';
import { useInfiniteScroll } from '../../../core/hooks/useInfiniteScroll';
import { RefreshBtn } from '../../../core/components/Button';
import Dropdown, { DropdownOption } from '../../../core/components/DropDown';
import './SyncLinksList.css';

interface SyncLinksListProps {
    className?: string;
}


interface SyncLinkCardProps {
    link: AdminUserServerLinkDto;
    innerRef?: (node: HTMLElement | null) => void;
}

const SyncLinkCard: React.FC<SyncLinkCardProps> = ({ link, innerRef }) => {
    const [expanded, setExpanded] = useState(false);

    const toggleExpand = () => setExpanded(!expanded);

    return (
        <div
            ref={innerRef}
            className={`sync-link-card ${expanded ? 'expanded' : ''}`}
        >
            <div className={`sync-link-status status-${link.status}`}></div>
            <div className="cu-inherit-container sync-link-info">
                <div className="sync-link-header-row" onClick={toggleExpand}>
                    <div className="sync-link-main">
                        <span className="sync-user">{link.tgUserName || link.userId}</span>
                        <span className="sync-server">Server: {link.serverId}</span>
                    </div>
                    <button className="expand-btn">
                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                </div>

                <div className="sync-link-details">
                    <div>Status: <b>{link.status}</b></div>
                    {link.lastAttempt && <div>Last Attempt: {new Date(link.lastAttempt).toLocaleString()}</div>}
                    {link.retries !== undefined && <div><b>Retries: {link.retries}</b></div>}

                    {!expanded && link.lastError && (
                        <div className="sync-error-msg" title={link.lastError}>
                            {link.lastError}
                        </div>
                    )}
                </div>

                {expanded && (
                    <div className="sync-link-full-details">
                        <div className="detail-group">
                            <strong>User ID:</strong>
                            <span>{link.userId}</span>
                        </div>

                        {link.lastError && (
                            <div className="detail-group error-group">
                                <strong>Last Error:</strong>
                                <pre className="cu-inherit-info error-text">{link.lastError}</pre>
                            </div>
                        )}

                        {link.requestDetails && (
                            <div className="detail-group">
                                <strong>Request Details:</strong>
                                <pre className="cu-inherit-info code-block">{link.requestDetails}</pre>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const createStatusOptions = (): DropdownOption[] => {
    return [
        { value: SyncStatus.SYNCED,   label: 'Success', color: 'var(--color-success)' },
        { value: SyncStatus.FAILED,   label: 'Failed',  color: 'var(--color-danger)'  },
        { value: SyncStatus.PENDING,  label: 'Pending', color: 'var(--color-warning)' },
    ];
};

export const SyncLinksList: React.FC<SyncLinksListProps> = ({ className = '' }) => {
    const restClient = useRestClient();
    const [links, setLinks] = useState<AdminUserServerLinkDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [syncStatusSelect, setSyncStatusSelect] = useState<SyncStatus | undefined>(SyncStatus.FAILED);

    const pageSize = 10;

    const fetchLinks = useCallback(async (page: number = 0, append: boolean = false) => {
        try {
            if (page === 0) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            setError(null);

            const response = await restClient.listSyncLinks({
                page,
                pageSize,
                status: syncStatusSelect
            });

            if (append) {
                setLinks(prev => [...prev, ...response.data]);
            } else {
                setLinks(response.data);
            }

            setTotal(response.total);
            setHasMore(response.hasMore);
            setCurrentPage(page);
        } catch (err) {
            setError('Ошибка загрузки списка синхронизации');
            console.error('Ошибка загрузки списка синхронизации:', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [restClient, pageSize, syncStatusSelect]);

    const loadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            fetchLinks(currentPage + 1, true);
        }
    }, [loadingMore, hasMore, currentPage, fetchLinks]);

    const lastElementRef = useInfiniteScroll({
        loading: loadingMore,
        hasMore,
        onLoadMore: loadMore
    });

    // initial load on mount
    useEffect(() => {
        fetchLinks(0, false);
    }, [fetchLinks]);

    const handleRefresh = () => {
        fetchLinks(0, false);
    };

    if (loading) {
        return (
            <div className={`sync-links-list ${className}`}>
                <div className="sync-links-header">
                    <div>
                        <h3>Синхронизация</h3>
                        <RefreshBtn onClick={handleRefresh} disabled />
                    </div>
                    <div className="loading">Загрузка данных...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`sync-links-list ${className}`}>
                <div className="sync-links-header">
                    <div>
                        <h3>Синхронизация</h3>
                        <RefreshBtn onClick={handleRefresh} showLabel />
                    </div>
                    <div className="error">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`cu-dashboard-container sync-links-list ${className}`}>
            <div className="sync-links-header">
                <div>
                    <h3>Синхронизация ({total})</h3>
                    {links.length > 0 && total > 0 && (
                        <div className="loading-progress">
                            <span>Загружено: {links.length} из {total}</span>
                        </div>
                    )}
                </div>
                <Dropdown onSelect={opt => { setSyncStatusSelect(opt?.value as SyncStatus) }} 
                    options={createStatusOptions()} />
                <div>

                </div>
                <div className="sync-links-actions">
                    <RefreshBtn onClick={handleRefresh} />
                </div>
            </div>

            <div className="sync-links-content">
                {links.length === 0 ? (
                    <div className="no-links">Записи не найдены</div>
                ) : (
                    <div className="sync-links-grid">
                        {links.map((link, index) => {
                            const isLastElement = index === links.length - 1;
                            const uniqueKey = `${link.userId}-${link.serverId}-${index}`;
                            return (
                                <SyncLinkCard
                                    key={uniqueKey}
                                    link={link}
                                    innerRef={isLastElement ? lastElementRef : undefined}
                                />
                            );
                        })}
                    </div>
                )}
                {loadingMore && (
                    <div className="loading-more">
                        <div className="loading-spinner">Загрузка...</div>
                    </div>
                )}
            </div>
        </div>
    );
};
